import { useEffect, useRef } from "react";

/**
 * Attaches an IntersectionObserver to the returned ref.
 * When the element enters the viewport it adds the class "revealed".
 *
 * If you pass `staggerChildren: true` the observer watches the ref element
 * itself AND queries all direct children with class "reveal-child", adding
 * "revealed" to each with an incremental delay.
 */
const useScrollReveal = (options = {}) => {
  const ref = useRef(null);
  const { staggerChildren = false, staggerDelay = 80, threshold = 0.05 } = options;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;

        if (staggerChildren) {
          // Reveal the container itself
          el.classList.add("revealed");
          // Then stagger each child
          const children = el.querySelectorAll(".reveal-child");
          children.forEach((child, i) => {
            setTimeout(() => {
              child.classList.add("revealed");
            }, i * staggerDelay);
          });
        } else {
          el.classList.add("revealed");
        }

        observer.unobserve(el);
      },
      { threshold }
    );

    observer.observe(el);
    return () => observer.disconnect();
  }, [staggerChildren, staggerDelay, threshold]);

  return ref;
};

export default useScrollReveal;
